const KEYS = {
  text: "vinci_pending_text",
  image: "vinci_pending_image",
  persona: "vinci_pending_persona",
  intensity: "vinci_pending_intensity",
  mode: "vinci_pending_mode",
  createSubTab: "vinci_pending_create_subtab",
  createPrompt: "vinci_pending_create_prompt",
  createOutputType: "vinci_pending_create_output_type",
} as const;

const CREATE_SUBTAB_STORAGE_KEY = "vml_create_subtab";

export type StudioSelectedMode = "write" | "create";
export type StudioCreateSubTab = "casual" | "business" | "repurpose" | "image";

const VALID_CREATE_SUB_TABS: StudioCreateSubTab[] = ["casual", "business", "repurpose", "image"];

export function readStoredCreateSubTab(): StudioCreateSubTab | null {
  try {
    const fromLs = localStorage.getItem(CREATE_SUBTAB_STORAGE_KEY) as StudioCreateSubTab | null;
    if (fromLs && VALID_CREATE_SUB_TABS.includes(fromLs)) return fromLs;
    const legacySs = sessionStorage.getItem(KEYS.createSubTab) as StudioCreateSubTab | null;
    if (legacySs && VALID_CREATE_SUB_TABS.includes(legacySs)) {
      localStorage.setItem(CREATE_SUBTAB_STORAGE_KEY, legacySs);
      return legacySs;
    }
  } catch {
    /* storage unavailable */
  }
  return null;
}

export function writeStoredCreateSubTab(tab: StudioCreateSubTab): void {
  try {
    localStorage.setItem(CREATE_SUBTAB_STORAGE_KEY, tab);
  } catch {
    /* quota exceeded — silent */
  }
}

/** Migrate legacy session storage values from the older Studio sub-tab schemas. */
function migrateLegacyMode(raw: string | null): { selectedMode: StudioSelectedMode; createSubTab: StudioCreateSubTab } {
  if (raw === "create") return { selectedMode: "create", createSubTab: "image" };
  if (raw === "transform") return { selectedMode: "write", createSubTab: "casual" };
  if (raw === "playful") return { selectedMode: "create", createSubTab: "casual" };
  if (raw === "write") {
    return { selectedMode: "write", createSubTab: "casual" };
  }
  if (raw === "code") {
    return { selectedMode: "write", createSubTab: "casual" };
  }
  return { selectedMode: "write", createSubTab: "casual" };
}

export function savePendingInput(state: {
  text: string;
  image: string | null;
  personaSlug: string;
  intensity: number;
  selectedMode: StudioSelectedMode;
  createSubTab: StudioCreateSubTab;
  createPrompt: string;
  createOutputType: string;
}) {
  try {
    sessionStorage.setItem(KEYS.text, state.text);
    sessionStorage.setItem(KEYS.persona, state.personaSlug);
    sessionStorage.setItem(KEYS.intensity, String(state.intensity));
    sessionStorage.setItem(KEYS.mode, state.selectedMode);
    writeStoredCreateSubTab(state.createSubTab);
    sessionStorage.setItem(KEYS.createPrompt, state.createPrompt);
    sessionStorage.setItem(KEYS.createOutputType, state.createOutputType);
    if (state.image) {
      sessionStorage.setItem(KEYS.image, state.image);
    } else {
      sessionStorage.removeItem(KEYS.image);
    }
  } catch { /* quota exceeded — silent */ }
}

export function getPendingInput(): {
  text: string;
  image: string | null;
  personaSlug: string;
  intensity: number;
  selectedMode: StudioSelectedMode;
  createSubTab: StudioCreateSubTab;
  createPrompt: string;
  createOutputType: string;
} | null {
  const text = sessionStorage.getItem(KEYS.text);
  const persona = sessionStorage.getItem(KEYS.persona);
  const intensity = sessionStorage.getItem(KEYS.intensity);
  if (text === null && persona === null && intensity === null) return null;

  const rawMode = sessionStorage.getItem(KEYS.mode);
  const { selectedMode, createSubTab: migratedSub } = migrateLegacyMode(rawMode);
  const storedSub = readStoredCreateSubTab();
  const createSubTab: StudioCreateSubTab = storedSub ?? migratedSub;

  return {
    text: text ?? "",
    image: sessionStorage.getItem(KEYS.image),
    personaSlug: persona ?? "",
    intensity: intensity ? Number(intensity) : 50,
    selectedMode,
    createSubTab,
    createPrompt: sessionStorage.getItem(KEYS.createPrompt) ?? "",
    createOutputType: sessionStorage.getItem(KEYS.createOutputType) ?? "writing",
  };
}

export function clearPendingInput() {
  Object.values(KEYS).forEach((k) => sessionStorage.removeItem(k));
}
